import React, { useEffect, useState } from "react";
import hrApi from "./hrApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { GitMerge, Trash2, FlaskConical, Loader2 } from "lucide-react";

function MergeGroup({ g, plan, setPlan }) {
  const p = plan[g.group_id];
  const set = (patch) => setPlan({ ...plan, [g.group_id]: { ...p, ...patch } });
  return (
    <div data-testid={`merge-group-${g.group_id}`} className={`border rounded-xl p-3 ${p.on ? "border-indigo-200 bg-indigo-50/30" : "border-slate-100"}`}>
      <div className="flex items-center gap-2">
        <input type="checkbox" className="w-4 h-4 accent-indigo-600" checked={p.on} onChange={(e) => set({ on: e.target.checked })} data-testid={`merge-on-${g.group_id}`} />
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${g.min_score >= 90 ? "bg-teal-100 text-teal-700" : "bg-amber-100 text-amber-700"}`}>{g.min_score}% match</span>
        <span className="text-xs text-slate-500">Keep as</span>
        <Input data-testid={`merge-name-${g.group_id}`} value={p.legal_name} onChange={(e) => set({ legal_name: e.target.value })} className="h-8 text-sm flex-1" />
      </div>
      <div className="mt-2 grid sm:grid-cols-2 gap-1.5">
        {g.members.map((m) => (
          <label key={m.id} className="flex items-center gap-2 text-sm border border-slate-100 rounded-lg px-2 py-1.5 bg-white cursor-pointer">
            <input type="radio" name={`keep-${g.group_id}`} checked={p.keep_id === m.id} onChange={() => set({ keep_id: m.id })} data-testid={`merge-keep-${m.id}`} />
            <span className="font-semibold text-slate-800 truncate">{m.name}</span>
            <span className="ml-auto text-[11px] text-slate-400 shrink-0">{m.kronos ? `K${m.kronos} · ` : ""}{m.doc_count} docs · {m.completeness}/9</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function TestPurge() {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState({});
  const [word, setWord] = useState("");
  const load = () => hrApi.get("/cleanup/test-records").then((r) => setRows(r.data));
  const purge = async () => {
    try { const { data } = await hrApi.post("/cleanup/purge-test", { ids: Object.keys(sel).filter((k) => sel[k]), confirm: word }); toast.success(`${data.purged} test record(s) purged`); setWord(""); setSel({}); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Purge failed"); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="test-purge">
      <div className="flex items-center gap-2"><FlaskConical size={16} className="text-slate-500" /><h3 className="font-heading font-bold text-slate-800">Test records</h3>
        {!rows && <button onClick={load} data-testid="test-purge-show" className="ml-auto text-xs text-indigo-600">Show</button>}</div>
      {rows && <>
        <p className="text-xs text-slate-500 mt-1">Permanently removes the test employee and every related record. Download their compliance pack first if you need it. Logged in the Deletion log.</p>
        <div className="space-y-1 mt-2">{rows.length === 0 && <div className="text-sm text-slate-400">No test records.</div>}
          {rows.map((r) => <label key={r.id} className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid={`test-sel-${r.id}`} checked={!!sel[r.id]} onChange={(e) => setSel({ ...sel, [r.id]: e.target.checked })} className="w-4 h-4 accent-rose-600" /> {r.name} <span className="text-xs text-slate-400">{r.doc_count} docs{r.deleted ? " · deleted" : ""}</span></label>)}</div>
        {rows.length > 0 && <div className="flex gap-2 mt-3"><Input data-testid="test-purge-word" value={word} onChange={(e) => setWord(e.target.value)} placeholder="Type PURGE" className="h-9 w-40" />
          <Button data-testid="test-purge-btn" disabled={word !== "PURGE" || !Object.values(sel).some(Boolean)} onClick={purge} className="h-9 rounded-full bg-rose-500 hover:bg-rose-400 text-white">Purge selected</Button></div>}
      </>}
    </div>
  );
}

export function CleanupReview() {
  const [d, setD] = useState(null);
  const [junk, setJunk] = useState({});
  const [plan, setPlan] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => hrApi.get("/cleanup/proposals").then((r) => {
    setD(r.data);
    setJunk(Object.fromEntries(r.data.junk.map((j) => [j.id, true])));
    setPlan(Object.fromEntries(r.data.merges.map((g) => [g.group_id, { on: g.min_score >= 90, keep_id: g.keep_id, legal_name: g.legal_name }])));
  });
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-sm text-slate-400 p-4">Scanning employee list…</div>;
  const chosen = d.merges.filter((g) => plan[g.group_id]?.on);
  const junkIds = Object.keys(junk).filter((k) => junk[k]);
  const apply = async () => {
    if (!window.confirm(`Remove ${junkIds.length} junk row(s) and merge ${chosen.length} group(s)? Merges move all documents, leave and history to the kept record.`)) return;
    setBusy(true);
    try {
      const merges = chosen.map((g) => ({ keep_id: plan[g.group_id].keep_id, legal_name: plan[g.group_id].legal_name, loser_ids: g.members.map((m) => m.id).filter((id) => id !== plan[g.group_id].keep_id) }));
      const { data } = await hrApi.post("/cleanup/apply", { junk_ids: junkIds, merges });
      toast.success(`${data.deleted} junk removed · ${data.merged} merged`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cleanup failed"); } setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="cleanup-review">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex items-center gap-2 flex-wrap"><GitMerge size={18} className="text-indigo-500" /><h3 className="font-heading font-bold text-slate-800">Clean-up review</h3>
          <span className="text-xs text-slate-500">{d.junk.length} junk row(s) · {d.merges.length} proposed merge(s). Nothing changes until you apply.</span>
          <Button data-testid="cleanup-apply" disabled={busy || (!chosen.length && !junkIds.length)} onClick={apply} className="ml-auto rounded-full bg-indigo-500 hover:bg-indigo-400 text-white h-9 gap-1">{busy && <Loader2 size={14} className="animate-spin" />} Apply {junkIds.length + chosen.length} change(s)</Button></div>
        {d.junk.length > 0 && <div className="mt-3 space-y-1" data-testid="junk-list"><div className="text-xs font-semibold text-slate-500 inline-flex items-center gap-1"><Trash2 size={12} /> Junk rows (soft-deleted, logged)</div>
          {d.junk.map((j) => <label key={j.id} className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid={`junk-${j.id}`} checked={!!junk[j.id]} onChange={(e) => setJunk({ ...junk, [j.id]: e.target.checked })} className="w-4 h-4 accent-rose-600" /><b>{j.name}</b><span className="text-xs text-slate-400">{j.reason} · {j.doc_count} docs</span></label>)}</div>}
        <div className="mt-3 flex gap-3 text-xs"><button onClick={() => setPlan(Object.fromEntries(Object.entries(plan).map(([k, v]) => [k, { ...v, on: true }])))} className="text-indigo-600">Select all</button>
          <button onClick={() => setPlan(Object.fromEntries(Object.entries(plan).map(([k, v]) => [k, { ...v, on: false }])))} className="text-slate-500">Select none</button></div>
        <div className="mt-2 space-y-2">{d.merges.map((g) => <MergeGroup key={g.group_id} g={g} plan={plan} setPlan={setPlan} />)}</div>
      </div>
      <TestPurge />
    </div>
  );
}
